import { useMemo, useState } from 'react'
import type { CloudReceiptsReport, CloudReceiptRow, CloudShiftRow } from '@billiard/shared'
import { useFetch } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  EmptyState,
  Input,
  Kpi,
  PageHead,
  SectionHead,
  Select
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { count, dateInputToMs, dateOnly, dateTime, money, msToDateInput } from '@/lib/format'
import { Cur } from '@/lib/currency'

// Дубль терминального экрана «Продажи» (apps/terminal/.../pages/Sales.tsx) в
// бэкофисе: те же четыре вкладки и те же номера чеков. На терминале экран
// остаётся — здесь владелец смотрит те же данные удалённо, по всем сменам.
//
// Отличие в источнике: терминал грузит 500 смен и детализацию каждой локально,
// а бэкофис берёт готовую выборку за период одним запросом
// (GET /v1/reports/receipts) — по сети иначе было бы сотни запросов.

type Tab = 'receipts' | 'revenue' | 'methods' | 'zreports'

const DAY_MS = 24 * 60 * 60 * 1000

const METHOD_LABEL: Record<string, string> = {
  cash: 'Наличные',
  card: 'Безналичные',
  mixed: 'Сборная',
  unknown: 'Не указан'
}

function methodShort(m: string | null): string {
  return m === 'cash' ? 'НАЛ' : m === 'card' ? 'БЕЗНАЛ' : m === 'mixed' ? 'СБОРНАЯ' : '—'
}

function timeOnly(ms: number): string {
  return new Date(ms).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

export function Sales(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('receipts')
  // Период по умолчанию — последние 30 дней; конец периода включает сутки целиком.
  const [from, setFrom] = useState(msToDateInput(Date.now() - 30 * DAY_MS))
  const [to, setTo] = useState(msToDateInput(Date.now()))
  const [table, setTable] = useState('')
  const [num, setNum] = useState('')

  const query = useConfigQuery()
  const fromMs = dateInputToMs(from)
  const toMs = dateInputToMs(to) + DAY_MS - 1
  const period = { ...query, from: fromMs, to: toMs }
  const report = useFetch<CloudReceiptsReport>('/v1/reports/receipts', period, [
    query?.clubId,
    fromMs,
    toMs
  ])

  const rows = report.data?.items ?? []
  const tables = useMemo(
    () => [...new Set(rows.map((r) => r.tableName).filter(Boolean))].sort(),
    [rows]
  )
  // Фильтры по столу и номеру — клиентские: выборка за период уже в руках,
  // ходить за ней снова на сервер незачем.
  const filtered = useMemo(() => {
    let r = rows
    if (num.trim()) r = r.filter((x) => x.receiptNumber.includes(num.trim()))
    if (table) r = r.filter((x) => x.tableName === table)
    return r
  }, [rows, num, table])

  const totals = useMemo(
    () => ({
      count: filtered.length,
      amount: filtered.reduce((s, r) => s + r.amount, 0),
      cash: filtered.reduce((s, r) => s + r.cashAmount, 0),
      card: filtered.reduce((s, r) => s + r.cardAmount, 0)
    }),
    [filtered]
  )

  const reset = (): void => {
    setNum('')
    setTable('')
    setFrom(msToDateInput(Date.now() - 30 * DAY_MS))
    setTo(msToDateInput(Date.now()))
  }

  return (
    <>
      <PageHead
        eyebrow="Отчёты"
        title="Продажи"
        lead="Чеки, выручка, методы оплаты и Z-отчёты по всем сменам клуба. Номера чеков совпадают с терминальными: «номер смены — номер чека»."
      />
      <ClubGate>
        <div className="toolbar">
          <div className="tabs">
            <button
              className={`tab${tab === 'receipts' ? ' is-active' : ''}`}
              onClick={() => setTab('receipts')}
            >
              Чеки
            </button>
            <button
              className={`tab${tab === 'revenue' ? ' is-active' : ''}`}
              onClick={() => setTab('revenue')}
            >
              Выручка
            </button>
            <button
              className={`tab${tab === 'methods' ? ' is-active' : ''}`}
              onClick={() => setTab('methods')}
            >
              Методы оплаты
            </button>
            <button
              className={`tab${tab === 'zreports' ? ' is-active' : ''}`}
              onClick={() => setTab('zreports')}
            >
              Z-Отчёты
            </button>
          </div>
        </div>

        {tab !== 'zreports' && (
          <>
            <div className="toolbar">
              <div
                className="toolbar__left"
                style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
              >
                {tab === 'receipts' && (
                  <>
                    <Input
                      style={{ width: 130 }}
                      placeholder="№ чека"
                      value={num}
                      onChange={(e) => setNum(e.target.value)}
                    />
                    <Select
                      style={{ width: 170 }}
                      value={table}
                      onChange={(e) => setTable(e.target.value)}
                    >
                      <option value="">Все столы</option>
                      {tables.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Select>
                  </>
                )}
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                <span className="muted">—</span>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                <Button variant="ghost" size="sm" onClick={reset}>
                  ✕ Сбросить
                </Button>
              </div>
            </div>

            <div className="kpi-grid">
              <Kpi label="Чеков" value={count(totals.count)} />
              <Kpi
                label="Сумма"
                value={
                  <>
                    {money(totals.amount)} <Cur />
                  </>
                }
              />
              <Kpi
                label="Наличные"
                value={
                  <>
                    {money(totals.cash)} <Cur />
                  </>
                }
              />
              <Kpi
                label="Безнал"
                value={
                  <>
                    {money(totals.card)} <Cur />
                  </>
                }
              />
            </div>

            <AsyncState query={report} />
          </>
        )}

        {tab === 'receipts' && !report.loading && !report.error && (
          <ReceiptsTable rows={filtered} totals={totals} />
        )}
        {tab === 'revenue' && !report.loading && !report.error && (
          <RevenueTable report={report.data} />
        )}
        {tab === 'methods' && !report.loading && !report.error && (
          <MethodsTable report={report.data} />
        )}
        {tab === 'zreports' && <ZReports />}
      </ClubGate>
    </>
  )
}

function ReceiptsTable({
  rows,
  totals
}: {
  rows: CloudReceiptRow[]
  totals: { count: number; amount: number; cash: number; card: number }
}): React.JSX.Element {
  if (rows.length === 0) {
    return <EmptyState message="За выбранный период чеков нет." />
  }
  return (
    <div className="table-wrap">
      <table className="rtable">
        <thead>
          <tr>
            <th>№ чека</th>
            <th>Дата</th>
            <th>Стол</th>
            <th>Тариф</th>
            <th>Начало</th>
            <th>Конец</th>
            <th className="num">Мин</th>
            <th>Метод</th>
            <th className="num">Нал</th>
            <th className="num">Безнал</th>
            <th className="num">Итого</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mono">{r.receiptNumber}</td>
              <td>{dateOnly(r.endedAt)}</td>
              <td>{r.tableName || '—'}</td>
              <td>{r.tariffName || '—'}</td>
              <td>{timeOnly(r.startedAt)}</td>
              <td>{timeOnly(r.endedAt)}</td>
              <td className="num">{count(r.durationMin)}</td>
              <td>{methodShort(r.paymentMethod)}</td>
              <td className="num">{r.cashAmount > 0 ? money(r.cashAmount) : '—'}</td>
              <td className="num">{r.cardAmount > 0 ? money(r.cardAmount) : '—'}</td>
              <td className="num">{money(r.amount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={8}>Итого: {count(totals.count)}</td>
            <td className="num">{money(totals.cash)}</td>
            <td className="num">{money(totals.card)}</td>
            <td className="num">{money(totals.amount)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function RevenueTable({ report }: { report: CloudReceiptsReport | null }): React.JSX.Element {
  const days = report?.byDay ?? []
  if (days.length === 0) return <EmptyState message="За выбранный период выручки нет." />
  const max = Math.max(...days.map((d) => d.amount), 0)
  return (
    <>
      <SectionHead title="Выручка по дням" />
      <div className="table-wrap">
        <table className="rtable">
          <thead>
            <tr>
              <th>День</th>
              <th className="num">Чеков</th>
              <th className="num">Нал</th>
              <th className="num">Безнал</th>
              <th className="num">Выручка</th>
              <th style={{ width: '30%' }} />
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.day}>
                <td>{d.day.split('-').reverse().join('.')}</td>
                <td className="num">{count(d.count)}</td>
                <td className="num">{money(d.cash)}</td>
                <td className="num">{money(d.card)}</td>
                <td className="num">
                  {money(d.amount)} <Cur />
                </td>
                <td>
                  {/* Полоса вместо графика: одна зависимость меньше, читается так же. */}
                  <div
                    style={{
                      height: 8,
                      borderRadius: 4,
                      background: 'var(--accent, #c9a84c)',
                      opacity: 0.75,
                      width: max > 0 ? `${Math.max(2, Math.round((d.amount / max) * 100))}%` : '2%'
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function MethodsTable({ report }: { report: CloudReceiptsReport | null }): React.JSX.Element {
  const rows = report?.byMethod ?? []
  const total = report?.totals.amount ?? 0
  if (rows.length === 0) return <EmptyState message="За выбранный период оплат нет." />
  return (
    <>
      <SectionHead title="Разбивка по способу оплаты" />
      <div className="table-wrap">
        <table className="rtable">
          <thead>
            <tr>
              <th>Способ</th>
              <th className="num">Чеков</th>
              <th className="num">Нал</th>
              <th className="num">Безнал</th>
              <th className="num">Сумма</th>
              <th className="num">Доля</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.method}>
                <td>{METHOD_LABEL[m.method] ?? m.method}</td>
                <td className="num">{count(m.count)}</td>
                <td className="num">{money(m.cash)}</td>
                <td className="num">{money(m.card)}</td>
                <td className="num">
                  {money(m.amount)} <Cur />
                </td>
                <td className="num">
                  {total > 0 ? `${Math.round((m.amount / total) * 100)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted" style={{ marginTop: 8 }}>
        «Сборная» — чек, оплаченный частично наличными и частично картой, поэтому суммы в столбцах
        «Нал» и «Безнал» внутри такой строки делятся между собой.
      </p>
    </>
  )
}

/**
 * Z-отчёты: закрытые смены. Открытая смена — это ещё X-отчёт, её показываем
 * отдельной пометкой и не считаем итоговой.
 */
function ZReports(): React.JSX.Element {
  const query = useConfigQuery()
  const shifts = useFetch<CloudShiftRow[]>('/v1/reports/shifts', query, [query?.clubId])
  const rows = shifts.data ?? []
  const closed = rows.filter((s) => s.closedAt != null)

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">
            {closed.length} закрыт. из {rows.length}
          </span>
        </div>
      </div>
      <AsyncState query={shifts} isEmpty={rows.length === 0} empty="Смен пока не было." />
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Смена</th>
                <th>Открыта</th>
                <th>Закрыта</th>
                <th className="num">Касса на начало</th>
                <th className="num">Нал</th>
                <th className="num">Безнал</th>
                <th className="num">Инкассация</th>
                <th className="num">Выручка</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td className="mono">№{s.number}</td>
                  <td>{dateTime(s.openedAt)}</td>
                  <td>
                    {s.closedAt ? dateTime(s.closedAt) : <span className="muted">открыта</span>}
                  </td>
                  <td className="num">{money(s.openingCash)}</td>
                  <td className="num">{money(s.cashTotal)}</td>
                  <td className="num">{money(s.cardTotal)}</td>
                  <td className="num">{s.encashment > 0 ? money(s.encashment) : '—'}</td>
                  <td className="num">
                    {money(s.revenue)} <Cur />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>Итого по закрытым сменам</td>
                <td className="num">{money(closed.reduce((a, s) => a + s.cashTotal, 0))}</td>
                <td className="num">{money(closed.reduce((a, s) => a + s.cardTotal, 0))}</td>
                <td className="num">{money(closed.reduce((a, s) => a + s.encashment, 0))}</td>
                <td className="num">{money(closed.reduce((a, s) => a + s.revenue, 0))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  )
}
